import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";
import { photoSrc } from "@/components/news/newsUtils";
import { PhotoUploader } from "./PhotoUploader";

const TextIn = ({ label, value, onChange, max, testId, placeholder }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold text-navy">{label}</span>
    <input className="field-input !py-2 text-sm" value={value} maxLength={max} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
  </label>
);

// One photo with alt text + caption, plus optional reorder/replace/remove controls.
export const PhotoItem = ({ p, onChange, onRemove, onMove, index, count, testId }) => (
  <li className="grid gap-4 border border-navy/10 bg-white p-4 sm:grid-cols-[140px_1fr]" data-testid={testId}>
    <img src={photoSrc(p.url_sm)} alt="" className="aspect-[3/2] w-full bg-mist object-cover sm:w-[140px]" />
    <div className="space-y-3">
      <TextIn label="Describe the photo for screen readers (alt text)" value={p.alt} max={200} onChange={(alt) => onChange({ ...p, alt })}
        placeholder="e.g. Canteen 33 crew serving firefighters at night" testId={`${testId}-alt`} />
      <TextIn label="Caption (optional, shown under the photo)" value={p.caption} max={300} onChange={(caption) => onChange({ ...p, caption })} testId={`${testId}-caption`} />
      <div className="flex flex-wrap gap-2">
        {onMove && (
          <>
            <button type="button" disabled={index === 0} onClick={() => onMove(-1)} className="btn btn-ghost !px-3 !py-1.5 text-xs disabled:opacity-40" aria-label="Move earlier" data-testid={`${testId}-up`}><ArrowUp size={14} /> Earlier</button>
            <button type="button" disabled={index === count - 1} onClick={() => onMove(1)} className="btn btn-ghost !px-3 !py-1.5 text-xs disabled:opacity-40" aria-label="Move later" data-testid={`${testId}-down`}><ArrowDown size={14} /> Later</button>
          </>
        )}
        <PhotoUploader label="Replace" variant="btn-ghost !px-3 !py-1.5 text-xs" testId={`${testId}-replace`}
          onUploaded={(np) => onChange({ ...p, ...np })} />
        <button type="button" onClick={onRemove} className="btn btn-ghost !px-3 !py-1.5 text-xs" data-testid={`${testId}-remove`}><Trash2 size={14} /> Remove</button>
      </div>
    </div>
  </li>
);

const blank = (np) => ({ alt: "", caption: "", ...np });

export const FeaturedPhoto = ({ value, onChange }) => (
  <div data-testid="editor-featured">
    {value ? (
      <ul><PhotoItem p={value} onChange={onChange} onRemove={() => onChange(null)} testId="editor-featured-photo" /></ul>
    ) : (
      <PhotoUploader label="Upload Featured Photo" testId="editor-featured-upload" onUploaded={(np) => onChange(blank(np))} />
    )}
  </div>
);

export const GalleryManager = ({ value, onChange }) => {
  const move = (i, d) => {
    const next = [...value];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    onChange(next);
  };
  return (
    <div data-testid="editor-gallery">
      {value.length > 0 && (
        <ul className="mb-4 space-y-3">
          {value.map((p, i) => (
            <PhotoItem key={`${p.photo}-${i}`} p={p} index={i} count={value.length} testId={`editor-gallery-photo-${i}`}
              onChange={(np) => onChange(value.map((x, j) => (j === i ? np : x)))}
              onRemove={() => onChange(value.filter((_, j) => j !== i))} onMove={(d) => move(i, d)} />
          ))}
        </ul>
      )}
      <PhotoUploader multiple label="Add Gallery Photos" testId="editor-gallery-upload"
        onUploaded={(np) => onChange((prev) => [...prev, blank(np)])} />
      <p className="mt-2 text-xs text-slate">Choose several at once. JPG, PNG or WebP, up to 15 MB each. Photos are resized automatically.</p>
    </div>
  );
};
